import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FieldValues } from 'react-hook-form';
import Card from '@/components/Card';
import { Input } from '@/components/Controls/Input';
import { Datatable } from '@/components/Datatable/Datatable';
import { TableViewProps } from '@/type/listing.types';
import { TableTitle } from '@/components/Header/TableTitle';
import Loader from '@/components/Loading/Loader';
import { ClearButton } from '@/components/Custom/Button/ClearButton';
import { useHistoryFilter } from '@/hooks/filters/history-filter.hooks';
import { HistoryType } from '@/entities/history';
import { useListAllGroups } from '@/hooks/group.hooks';
import { useListAllCompanies } from '@/hooks/company.hooks';
import { useListAllBranches } from '@/hooks/branch.hooks';
import { mapFilterSelect } from '@/utils/map.utils';
import {
  getOptionsVehiculeCheckType,
  getOptionProVehiculeCheckStatus,
  getOptionIndividualVehiculeCheckStatus,
  getInspectionModerationStatus,
  getInspectionValidationStatus,
} from '@/utils/constant.utils';
import { Select } from '@/components/Controls/Select';
import ButtonAdvancedFilter from '@/components/Custom/Button/ButtonAdvancedFilter';
import VehicleCheckFilter from '@/components/Custom/Filter/VehicleCheckFilter';
import { useUserRole } from '@/hooks/auth.hooks';
import { ActionUserType } from '@/api/entities/vehicule-check';

const limit = 20;

interface VehiculeCheckTableViewProps<
  TSchema extends FieldValues,
> extends TableViewProps<TSchema> {
  cardTitle?: React.ReactNode;
  receivedVehiculeId?: string;
  receivedActionUserId?: string;
}

export const VehiculeCheckTableView = <TSchema extends FieldValues>({
  t,
  useGetAllData,
  onGetTotalActive,
  tableHeaders: TableHeaders,
  tableData: TableData,
  pendingDeactivate,
  onDeactivate,
  canSelectItems = true,
  cardTitle,
  receivedVehiculeId,
  receivedActionUserId,
}: VehiculeCheckTableViewProps<TSchema>) => {
  let { vehiculeId } = useParams();

  const isRequestListPage = location.pathname.startsWith(
    '/vehicule-check-requests',
  );

  if (receivedVehiculeId) {
    vehiculeId = receivedVehiculeId;
  }

  const [totalPages, setTotalPages] = useState<number>();
  const { isAdmin, isGroupManager, isCompanyManager } = useUserRole();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  /**
   * === Filter Section ===
   */
  const {
    debouncedFilter,
    setDebouncedFilter,
    filtersEmpty,
    clearFilters,
    key,
    startDate,
    endDate,
    groupId,
    companyId,
    branchId,
    clientType,
    progressStatus,
    humanModerationStatus,
    reportValidated,
    searchByKey,
    searchStartDate,
    searchEndDate,
    searchByGroup,
    searchByCompany,
    searchByBranch,
    searchByClientType,
    searchByProgressStatus,
    searchByHumanModerationStatus,
    searchByReportValidated,
  } = useHistoryFilter({
    historyType: HistoryType.VEHICULE_CHECK,
    setCurrentPage,
  });

  useEffect(() => {
    setDebouncedFilter((prev) => ({
      ...prev,
      vehiculeId,
      actionUserId: receivedActionUserId,
      groupId,
      companyId,
      branchId,
      clientType: vehiculeId
        ? prev?.clientType
        : clientType
          ? clientType
          : isRequestListPage
            ? ActionUserType.INDIVIDUAL
            : undefined,
      isRequestList: isRequestListPage,
      progressStatus,
      humanModerationStatus,
      reportValidated,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isRequestListPage,
    receivedVehiculeId,
    receivedActionUserId,
    groupId,
    companyId,
    branchId,
    clientType,
    progressStatus,
    humanModerationStatus,
    reportValidated,
  ]);
  /**
   * === END OF Filter Section ===
   */

  /**
   * === Data Fetching section ===
   */
  const {
    data: allData,
    isLoading: isLoadingAllData,
    isPending: isPendingAllData,
  } = useGetAllData(
    debouncedFilter,
    currentPage,
    limit,
    sortColumn,
    sortDirection,
  );

  const { data: allGroups, isLoading: isLoadingAllGroups } = useListAllGroups();
  const { data: allCompanies, isLoading: isLoadingAllCompanies } =
    useListAllCompanies({
      groupId: debouncedFilter?.groupId,
    });
  const { data: allBranches, isLoading: isLoadingAllBranches } =
    useListAllBranches({
      groupId: debouncedFilter?.groupId,
      companyId: debouncedFilter?.companyId,
    });
  /**
   * === END OF Data Fetching section ===
   */

  /**
   * === Pagination Section ===
   */
  useEffect(() => {
    setTotalPages(allData?.data?.totalPages);
    onGetTotalActive?.(allData?.data?.['total']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData]);
  /**
   * === END OF Pagination Section ===
   */

  /**
   * === Sorting Section ===
   */
  const handleSort = (column: string) => {
    setSortColumn(column);
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };
  /**
   * === END OF Sorting Section ===
   */

  return (
    <>
      <Card bodyClassName="flex flex-col gap-5" title={cardTitle}>
        <div className="flex flex-wrap items-center gap-5">
          <div className="flex flex-1">
            <TableTitle
              text={
                isRequestListPage
                  ? t('common:vehiculeCheckRequests')
                  : t('common:inspections')
              }
            />
          </div>
          <div className="flex flex-wrap gap-5">
            <Input
              placeholder={t('common:searchLicensePlateOrVin')}
              type="text"
              name="search"
              autoComplete="off"
              leftIcon={
                <i className="ki-outline ki-magnifier text-lg text-gray-8990A8" />
              }
              containerClassName="max-[1323px]:flex-none max-[1323px]:w-full"
              inputWrapperClassName="w-[315px] max-[1323px]:w-full"
              inputClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
              value={key}
              onChange={(e) => searchByKey(e.target.value)}
            />
            {!isRequestListPage && (
              <Select
                containerClassName="max-[1323px]:flex-none max-w-60 max-[1323px]:w-full"
                selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500 max-[1323px]:max-w-full"
                options={getOptionsVehiculeCheckType(t)}
                value={clientType}
                onChange={(e) => searchByClientType(e)}
              />
            )}
            <Select
              containerClassName="max-[1323px]:flex-none max-w-60 max-[1323px]:w-full"
              selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500 max-[1323px]:max-w-full"
              options={
                isRequestListPage
                  ? getOptionIndividualVehiculeCheckStatus(t)
                  : getOptionProVehiculeCheckStatus(t)
              }
              value={progressStatus}
              onChange={(e) => searchByProgressStatus(e)}
            />
            <div className="flex justify-end items-center gap-5 max-[1323px]:flex-1">
              <ButtonAdvancedFilter>
                <VehicleCheckFilter
                  t={t}
                  isLoadingAllGroups={isLoadingAllGroups}
                  isLoadingAllCompanies={isLoadingAllCompanies}
                  isLoadingAllBranches={isLoadingAllBranches}
                  humanModerationStatusOptions={getInspectionModerationStatus(
                    t,
                  )}
                  reportValidatedOptions={getInspectionValidationStatus(t)}
                  groupOptions={mapFilterSelect(allGroups?.data)}
                  companyOptions={mapFilterSelect(allCompanies?.data)}
                  branchOptions={mapFilterSelect(allBranches?.data)}
                  displayGroupFilter={isAdmin || isGroupManager}
                  displayCompanyFilter={
                    isAdmin || isGroupManager || isCompanyManager
                  }
                  displayBranchFilter
                  humanModerationStatus={humanModerationStatus}
                  reportValidated={reportValidated}
                  groupId={groupId}
                  companyId={companyId}
                  branchId={branchId}
                  startDate={startDate}
                  endDate={endDate}
                  searchStartDate={searchStartDate}
                  searchEndDate={searchEndDate}
                  searchByHumanModerationStatus={searchByHumanModerationStatus}
                  searchByReportValidated={searchByReportValidated}
                  searchByGroup={searchByGroup}
                  searchByCompany={searchByCompany}
                  searchByBranch={searchByBranch}
                />
              </ButtonAdvancedFilter>
              <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
            </div>
          </div>
        </div>
        <div>
          <Datatable
            t={t}
            headers={
              <TableHeaders
                t={t}
                onSort={handleSort}
                sortDirection={sortDirection}
              />
            }
            data={
              isLoadingAllData ? (
                <></>
              ) : !allData?.data?.data?.length ? null : (
                <TableData
                  t={t}
                  data={allData?.data?.data}
                  canSelectItems={canSelectItems}
                  pendingDeactivate={pendingDeactivate}
                  onDeactivate={onDeactivate}
                />
              )
            }
            canSelectItems={canSelectItems}
            showPagination={allData?.data?.data?.length > 0 && totalPages > 1}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </Card>
      {(isLoadingAllData || isPendingAllData || pendingDeactivate) && (
        <Loader />
      )}
    </>
  );
};
